/**
 * Offline persistence layer for DocuConvert.
 * Stores: file metadata list + downloaded blobs in IndexedDB so that
 *  - Dashboard shows recent files even without network
 *  - Already-viewed files can be re-downloaded offline
 */
const DB_NAME = "docuconvert";
const VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const r = indexedDB.open(DB_NAME, VERSION);
    r.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("files")) {
        const s = db.createObjectStore("files", { keyPath: "id" });
        s.createIndex("user", "user_id", { unique: false });
      }
      if (!db.objectStoreNames.contains("blobs")) {
        db.createObjectStore("blobs", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
    };
    r.onsuccess = (e) => resolve(e.target.result);
    r.onerror = () => reject(r.error);
  });
}

async function withStore(name, mode, work) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, mode);
    const store = tx.objectStore(name);
    let result;
    try {
      result = work(store);
    } catch (e) {
      reject(e);
      return;
    }
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function reqAsync(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---- Files (metadata) ----
export async function saveFiles(userId, files) {
  try {
    await withStore("files", "readwrite", (s) => {
      // Clear only this user's entries by reading + deleting matching ids
      const req = s.getAll();
      req.onsuccess = () => {
        const rows = req.result || [];
        rows.forEach((r) => {
          if (r.user_id === userId) s.delete(r.id);
        });
        files.forEach((f) => s.put({ ...f, user_id: userId, _cached_at: Date.now() }));
      };
    });
  } catch (e) {
    console.warn("[offline] saveFiles failed", e);
  }
}

export async function getCachedFiles(userId) {
  try {
    const all = await withStore("files", "readonly", (s) => reqAsync(s.getAll()));
    const list = (await all) || [];
    return list.filter((f) => f.user_id === userId);
  } catch {
    return [];
  }
}

export async function removeCachedFile(id) {
  try {
    await withStore("files", "readwrite", (s) => s.delete(id));
    await withStore("blobs", "readwrite", (s) => s.delete(id));
  } catch {}
}

// ---- Blobs ----
export async function saveBlob(id, blob, filename, contentType) {
  try {
    await withStore("blobs", "readwrite", (s) =>
      s.put({ id, blob, filename, content_type: contentType, saved_at: Date.now() })
    );
  } catch (e) {
    console.warn("[offline] saveBlob failed", e);
  }
}

export async function getBlob(id) {
  try {
    return await withStore("blobs", "readonly", (s) => reqAsync(s.get(id)));
  } catch {
    return null;
  }
}

// ---- Meta (last user, etc.) ----
export async function setMeta(key, value) {
  try {
    await withStore("meta", "readwrite", (s) => s.put({ key, value }));
  } catch {}
}

export async function getMeta(key) {
  try {
    const v = await withStore("meta", "readonly", (s) => reqAsync(s.get(key)));
    return v?.value;
  } catch {
    return null;
  }
}
